// Fees → Policies → New override. One guided page for both override
// kinds: pick what the override covers (a single market or a whole
// category), see what that target charges today, then set the new
// rates. The consequence line under the submit button says exactly
// what will happen before the operator commits.
//
// Currency is a first-class field since SM-510: a policy prices ONE
// currency, so it has to be chosen before anything can be saved, and the
// eligible targets narrow to the ones whose books can actually charge in
// it (AC 8). The "What this prices" panel reads the FORM's currency and
// target rather than a saved profile, so it shows what the operator is
// about to do.
import {
  createFileRoute,
  Link,
  useNavigate,
} from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  createFeeProfile,
  fetchCategories,
  fetchFeeProfiles,
  fetchRules,
  feeKeys,
  keys,
} from "@/lib/queries";
import type { MarketItem } from "@/lib/types";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Select } from "@/components/ui/Select";
import { FeeRateInput } from "@/components/fees/FeeRateInput";
import { ScheduleField } from "@/components/fees/ScheduleField";
import { BrokerCeilingHint } from "@/components/fees/BrokerCeilingHint";
import { MarketPickField } from "@/components/fees/MarketPickField";
import {
  fmtPct,
  redemptionRateOf,
  resolveEffectiveRate,
  setsRedemptionRate,
} from "@/components/fees/policy";
import {
  percentLabel,
  RedemptionFeeField,
  type RedemptionMode,
} from "@/components/fees/RedemptionFeeField";
import { useMyPerms } from "@/hooks/useMyPerms";
import { Perm } from "@/lib/perms";
import { useFeeReach } from "@/components/fees/useFeeReach";
import { FeeReachPanel } from "@/components/fees/FeeReachPanel";
import { FeePolicyCurrencyField } from "@/components/fees/FeePolicyCurrencyField";
import {
  EMPTY_REACH,
  exampleCurrency,
  reachAllowsCurrency,
  redemptionChargeNote,
} from "@/components/fees/feeCurrency";
import type { CurrencyCode } from "@/currency";
import { useCurrencyName } from "@/components/currency/CurrencyPresentationProvider";

// "platform" joins the two override kinds because SM-510 made the
// platform default a per-currency row: a catalog that has a Gold default
// and no Red one needs a way to CREATE the Red one, and the detail page
// can only edit a policy that already exists.
type OverrideScope = "market" | "category" | "platform";

export const Route = createFileRoute("/_app/treasury/fees/policies/new")({
  validateSearch: (search: Record<string, unknown>): { scope: OverrideScope } =>
    search.scope === "category"
      ? { scope: "category" }
      : search.scope === "platform"
      ? { scope: "platform" }
      : { scope: "market" },
  component: NewOverridePage,
});

const DEFAULT_PRECISION = 6;
const PAGE_FETCH_LIMIT = 200;

function NewOverridePage() {
  const { scope } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // The whole form is about ONE currency, chosen below. Everything it
  // says about what the target charges today — the current rate, what
  // "Inherit" is promising to keep, which targets already have an
  // override — has to be read in THAT currency, or the page would
  // describe the Gold chain while saving a Red policy.
  const [currency, setCurrency] = useState<CurrencyCode | null>(null);
  const currencyName = useCurrencyName(currency);

  const globalQ = useQuery({
    queryKey: feeKeys.profiles("GLOBAL", currency ?? undefined),
    queryFn: () => fetchFeeProfiles("GLOBAL", currency ?? undefined),
    staleTime: 30_000,
  });
  const categoryProfilesQ = useQuery({
    queryKey: feeKeys.profiles("CATEGORY", currency ?? undefined),
    queryFn: () => fetchFeeProfiles("CATEGORY", currency ?? undefined),
    staleTime: 30_000,
  });
  const marketProfilesQ = useQuery({
    queryKey: feeKeys.profiles("MARKET", currency ?? undefined),
    queryFn: () => fetchFeeProfiles("MARKET", currency ?? undefined),
    staleTime: 30_000,
  });
  const categoriesQ = useQuery({
    queryKey: keys.categoriesList(PAGE_FETCH_LIMIT, 0),
    queryFn: () => fetchCategories({ limit: PAGE_FETCH_LIMIT, offset: 0 }),
  });
  const marketsQ = useQuery({
    queryKey: keys.rulesList(
      "OPEN",
      PAGE_FETCH_LIMIT,
      0,
      { by: "created_at", dir: "desc" },
      {},
    ),
    queryFn: () =>
      fetchRules({
        limit: PAGE_FETCH_LIMIT,
        offset: 0,
        status: "OPEN",
        sort: { by: "created_at", dir: "desc" },
      }),
  });

  // A level answers with its own currency policy, or its legacy row —
  // the same order market-service resolves in.
  const globalProfile =
    globalQ.data?.find((p) => p.currency === currency) ??
    globalQ.data?.find((p) => !p.currency) ??
    null;
  const categoryProfiles = categoryProfilesQ.data ?? [];
  const marketProfiles = marketProfilesQ.data ?? [];
  const categories = categoriesQ.data?.items ?? [];
  const markets = marketsQ.data?.items ?? [];
  const precision = globalProfile?.fee_rate_precision ?? DEFAULT_PRECISION;

  // Which books each target runs — the answer to "can this target be
  // charged in the chosen currency at all" (AC 8) and to "what does this
  // rate price" in the panel.
  const reachIndex = useFeeReach();

  // Targets that already have an override IN THIS CURRENCY are edited
  // via their own row, not silently replaced from here. A market with a
  // Gold override is still a legitimate target for a Red one — that is
  // the whole point of the ticket — so the exclusion is per currency,
  // not per market.
  const overriddenMarketIds = useMemo(
    () =>
      new Set(
        marketProfiles.filter((p) => p.currency === currency).map((p) => p.scope_id),
      ),
    [marketProfiles, currency],
  );
  const eligibleCategories = useMemo(() => {
    const overridden = new Set(
      categoryProfiles.filter((p) => p.currency === currency).map((p) => p.scope_id),
    );
    // AC 8: a category whose markets can never be charged in this
    // currency is not an eligible target, and the empty state below
    // says which currency made the list empty.
    return categories.filter(
      (c) =>
        !overridden.has(c.category_id) &&
        reachAllowsCurrency(reachIndex.forScope("CATEGORY", c.category_id), currency ?? undefined),
    );
  }, [categories, categoryProfiles, currency, reachIndex]);

  const [market, setMarket] = useState<MarketItem | null>(null);
  const [categoryId, setCategoryId] = useState("");
  const [maker, setMaker] = useState("0");
  const [taker, setTaker] = useState("0");
  // A new override inherits the redemption fee by default. An operator
  // creating a taker-fee override for one market almost never means to
  // change what its winners are charged, and defaulting to "set" would
  // pin whatever number happened to be in the box.
  const [redeemMode, setRedeemMode] = useState<RedemptionMode>("inherit");
  const [redeem, setRedeem] = useState("0");
  const [note, setNote] = useState("");
  const [effectiveAt, setEffectiveAt] = useState("");

  // The platform default has one target and it is implicit — the
  // sentinel the server normalizes to. Nothing to pick.
  const targetId =
    scope === "platform"
      ? "_global"
      : scope === "market"
      ? market?.market_id ?? ""
      : categoryId;

  // Nothing picked yet has no reach at all — an unknown one, which the
  // panel renders as such.
  const reach =
    targetId === ""
      ? EMPTY_REACH
      : scope === "platform"
      ? reachIndex.forCatalog()
      : scope === "market" && market
      ? reachIndex.forMarketRow(market)
      : reachIndex.forScope("CATEGORY", targetId);
  // AC 9: the worked example is quoted in the currency this policy
  // prices, at that currency's own precision and rounding. Nothing to
  // preview until one is chosen.
  const exampleCurrencies = currency ? [currency] : [];
  // A market the operator picked that cannot be charged in the chosen
  // currency: the server refuses it (AC 8/10), so the form says so
  // instead of letting the save fail.
  const targetAllowsCurrency =
    scope !== "market" || !market || reachAllowsCurrency(reach, currency ?? undefined);

  const perms = useMyPerms();
  const canEditRedemption =
    !perms.loaded || perms.isSuperAdmin || perms.has(Perm.FeeConfigUpdate);

  // What the picked target pays on a redemption today — the number the
  // "Inherit" option is promising to keep. A market looks to its
  // category first; a category, and a market whose category sets none,
  // fall through to the platform.
  const inheritedRedemption = useMemo(() => {
    if (scope === "market" && market) {
      const cat = categoryProfiles.find(
        (p) => p.scope_id === market.category_id,
      );
      if (setsRedemptionRate(cat)) {
        const catName =
          categories.find((c) => c.category_id === market.category_id)?.name;
        return {
          rate: redemptionRateOf(cat),
          from: catName ? `the ${catName} category override` : "its category override",
        };
      }
    }
    return {
      rate: redemptionRateOf(globalProfile),
      from: setsRedemptionRate(globalProfile)
        ? "the platform default"
        : "the platform default, which sets none",
    };
  }, [scope, market, categoryProfiles, categories, globalProfile]);

  const mut = useMutation({
    mutationFn: () =>
      createFeeProfile({
        scope:
          scope === "platform"
            ? "GLOBAL"
            : scope === "market"
            ? "MARKET"
            : "CATEGORY",
        scope_id: targetId,
        // Non-null by the time this runs: the submit button is disabled
        // until a currency is chosen, and the server refuses a body
        // without one either way.
        currency: currency!,
        maker_fee_rate: maker || "0",
        taker_fee_rate: taker || "0",
        fee_rate_precision: precision,
        // Omitted when inheriting: a new profile with no redemption
        // rate is exactly what "inherit" means, and sending an explicit
        // clear would be the same thing said louder.
        ...(redeemMode === "set" ? { redemption_fee_rate: redeem || "0" } : {}),
        note: note.trim() || undefined,
        effective_at: effectiveAt || undefined,
      }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: feeKeys.all });
      qc.invalidateQueries({ queryKey: ["admin-rules"] });
      if (created?.profile_id) {
        navigate({
          to: "/treasury/fees/policies/$profileId",
          params: { profileId: created.profile_id },
          replace: true,
        });
      } else {
        navigate({ to: "/treasury/fees/policies" });
      }
    },
    meta: {
      successMessage: effectiveAt ? "Override scheduled." : "Override created.",
    },
  });

  // Consequence copy for the picked target. Silent until the chain
  // queries resolve: "currently fee-free" while loading would be a lie.
  const chainReady =
    globalQ.isSuccess && categoryProfilesQ.isSuccess && marketProfilesQ.isSuccess;
  const currentRate = useMemo(() => {
    if (!chainReady) return null;
    if (scope === "market" && market) {
      const eff = resolveEffectiveRate(
        market,
        marketProfiles,
        categoryProfiles,
        globalProfile,
        categories,
      );
      if (!eff.profile) return "This market is currently fee-free.";
      const effPrecision = eff.profile.fee_rate_precision ?? precision;
      return `Currently charges ${fmtPct(eff.profile.taker_fee_rate, effPrecision)} taker · ${fmtPct(eff.profile.maker_fee_rate, effPrecision)} maker via ${eff.sourceLabel}.`;
    }
    if (scope === "category" && categoryId) {
      const inCategory = markets.filter((m) => m.category_id === categoryId);
      const owned = inCategory.filter((m) =>
        overriddenMarketIds.has(m.market_id),
      ).length;
      const base = globalProfile
        ? `Markets in this category currently use the platform default (${fmtPct(globalProfile.taker_fee_rate, globalProfile.fee_rate_precision ?? precision)} taker).`
        : "Markets in this category are currently fee-free.";
      const reach =
        inCategory.length > 0
          ? ` The override will cover ${inCategory.length - owned} of ${inCategory.length} open markets${owned > 0 ? "; the rest keep their own override" : ""}.`
          : "";
      return base + reach;
    }
    return null;
  }, [
    chainReady,
    scope,
    market,
    categoryId,
    marketProfiles,
    categoryProfiles,
    globalProfile,
    categories,
    markets,
    overriddenMarketIds,
    precision,
  ]);

  const canSubmit =
    targetId !== "" && currency !== null && targetAllowsCurrency && !mut.isPending;

  // A platform default is never "inert": it is the terminal answer for
  // its currency and applies the moment any market opens a book that
  // charges it. So the AC-8 eligibility guard is a MARKET-scope rule.

  return (
    <div className="space-y-5">
      <div>
        <Link
          to="/treasury/fees/policies"
          className="text-2xs font-medium text-muted hover:text-fg"
        >
          ← Fee policies
        </Link>
        <h1 className="mt-2 font-display text-2xl tracking-tight">
          {scope === "platform" ? "New platform default" : "New fee override"}
        </h1>
        <p className="max-w-[75ch] text-sm text-muted">
          {scope === "platform"
            ? "Set the rate every market falls back to in one currency. Markets with their own policy, or their category's, keep it."
            : "Set a special rate for one market or a whole category. It beats the platform default from the moment it activates."}
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,360px)]">
        <Card className="self-start">
          <CardBody className="space-y-5">
            <SegmentedControl<OverrideScope>
              label="Covers"
              full
              options={[
                { value: "market", label: "One market" },
                { value: "category", label: "A whole category" },
                { value: "platform", label: "Every market" },
              ]}
              value={scope}
              onChange={(next) => {
                navigate({
                  to: "/treasury/fees/policies/new",
                  search: { scope: next },
                  replace: true,
                });
              }}
            />

            <FeePolicyCurrencyField
              value={currency}
              onChange={(next) => {
                setCurrency(next);
                // The eligible targets are per currency, so a target
                // picked under the old one may no longer be one. Clearing
                // it is the honest move: leaving it selected would let an
                // operator save a policy the picker would not have
                // offered them.
                setMarket(null);
                setCategoryId("");
              }}
            />

            {scope === "platform" ? (
              <p className="rounded-md border border-line-1 bg-surface-2 px-3 py-2.5 text-2xs leading-relaxed text-t-2">
                The platform default is the last step in the chain. It applies
                to every market that has no category or market policy of its
                own in this currency.
              </p>
            ) : scope === "market" ? (
              <MarketPickField
                value={market}
                onChange={setMarket}
                disabled={currency === null}
                excludeIds={overriddenMarketIds}
                excludeHint={
                  currencyName
                    ? `No eligible markets match. Markets that already have a ${currencyName} override are edited from their own row.`
                    : "No eligible markets match. Markets that already have an override are edited from their own row."
                }
              />
            ) : (
              <div className="flex flex-col gap-1.5">
                <label className="text-2xs font-semibold uppercase tracking-wide text-t-3">
                  Category
                </label>
                {categoriesQ.isLoading || categoryProfilesQ.isLoading ? (
                  <p className="rounded-md border border-line-1 bg-surface-2 px-3 py-2.5 text-2xs text-muted">
                    Loading categories…
                  </p>
                ) : categoriesQ.isError ? (
                  <p className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2.5 text-2xs text-t-2">
                    Couldn&apos;t load categories. Reload the page to try again.
                  </p>
                ) : currency === null ? (
                  <p className="rounded-md border border-line-1 bg-surface-2 px-3 py-2.5 text-2xs text-muted">
                    Choose a currency first. Which categories can be given a
                    policy depends on which books they run.
                  </p>
                ) : eligibleCategories.length === 0 ? (
                  <p className="rounded-md border border-line-1 bg-surface-2 px-3 py-2.5 text-2xs text-muted">
                    {currencyName
                      ? `No category can take a ${currencyName} policy. Either every category already has one, or none of them runs a book that charges it.`
                      : "Every category already has an override. Edit them from the Policies page."}
                  </p>
                ) : (
                  <Select
                    placeholder="Choose a category"
                    value={categoryId}
                    disabled={currency === null}
                    onChange={(e) => setCategoryId(e.target.value)}
                    options={eligibleCategories.map((c) => ({
                      value: c.category_id,
                      label: c.name,
                    }))}
                  />
                )}
              </div>
            )}

            {!targetAllowsCurrency && currencyName && (
              // One interpolated string, not prose around an expression:
              // the extractor that feeds the localized catalog splits a
              // JSX text node at every expression (the SM-354 note in
              // DESIGN.md), and this sentence would reach a Thai
              // operator as two unusable fragments.
              <p className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2.5 text-2xs leading-relaxed text-t-2">
                {`This market does not run a book that charges ${currencyName}, so a policy for it would price nothing. Turn on that book first, or choose a currency this market charges.`}
              </p>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <FeeRateInput
                label="Taker fee"
                value={taker}
                precision={precision}
                onChange={setTaker}
                currencies={exampleCurrencies}
              />
              <FeeRateInput
                label="Maker fee"
                value={maker}
                precision={precision}
                onChange={setMaker}
                showPreview={false}
                hint="Charged to the resting side of a fill. Usually zero."
              />
            </div>

            <RedemptionFeeField
              mode={redeemMode}
              onModeChange={setRedeemMode}
              value={redeem}
              onChange={setRedeem}
              precision={precision}
              // Nothing chosen yet previews in Gold, the currency every
              // market on the platform charges — the rest of the field
              // is disabled until a currency is picked anyway.
              currency={currency ?? exampleCurrency(undefined)}
              inheritedRate={inheritedRedemption.rate}
              inheritedFrom={inheritedRedemption.from}
              canInherit
              disabled={!canEditRedemption}
              disabledHint={
                canEditRedemption
                  ? undefined
                  : "Your role can view the redemption fee but not change it."
              }
            />

            {redemptionChargeNote(currency ?? exampleCurrency(undefined)) && (
              <p className="rounded-md border border-line-1 bg-surface-2 px-3 py-2.5 text-2xs leading-relaxed text-t-2">
                {redemptionChargeNote(currency ?? exampleCurrency(undefined))}
              </p>
            )}

            <ScheduleField value={effectiveAt} onChange={setEffectiveAt} />

            <div>
              <label
                htmlFor="new-override-note"
                className="text-2xs font-semibold uppercase tracking-wide text-t-3"
              >
                Note (audit log)
              </label>
              <input
                id="new-override-note"
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Why this override?"
                className="mt-1.5 h-10 w-full rounded-md border border-line-1 bg-bg-2 px-3 text-sm text-t-1 placeholder:text-t-4 focus:border-line-3 focus:outline-none focus:ring-4 focus:ring-neon/15"
              />
            </div>

            {mut.isError && (
              <p className="text-2xs text-danger">
                {(mut.error as Error).message}
              </p>
            )}
            {mut.isError && <BrokerCeilingHint error={mut.error} />}

            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={() => mut.mutate()}
                disabled={!canSubmit}
                loading={mut.isPending}
              >
                {effectiveAt
                  ? "Schedule change"
                  : scope === "platform"
                  ? "Create platform default"
                  : "Create override"}
              </Button>
              <Link
                to="/treasury/fees/policies"
                className="text-2xs font-medium text-muted hover:text-fg"
              >
                Cancel
              </Link>
            </div>
          </CardBody>
        </Card>

        <div className="space-y-5">
          <FeeReachPanel
            reach={reach}
            currency={currency ?? undefined}
            loading={reachIndex.loading}
            emptyDescription="This target has no book open, so the override charges nothing until one opens."
            unavailable={
              targetId === ""
                ? "Pick a currency and what the override covers to see where its rate is charged."
                : undefined
            }
          />

          {currentRate && (
            <Card>
              <CardBody className="space-y-1.5 text-2xs leading-relaxed text-t-2">
                <div className="font-semibold uppercase tracking-wide text-t-3">
                  Current rate
                </div>
                <p>{currentRate}</p>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardBody className="space-y-1.5 text-2xs leading-relaxed text-t-2">
              <div className="font-semibold uppercase tracking-wide text-t-3">
                Good to know
              </div>
              <p>
                Each policy sets the rate for one currency. The target keeps
                whatever its other currencies were already charging.
              </p>
              <p>
                A 0% override is a valid pin: it keeps the target fee-free even
                if the platform default changes later.
              </p>
              <p>
                The three fees resolve separately. Leaving Redeem on Inherit
                means this override moves the trading fees only, and the target
                keeps paying{" "}
                {percentLabel(inheritedRedemption.rate, precision)} on
                redemptions.
              </p>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
